import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plane, Plus, Check, X, Loader2 } from "lucide-react";

const statusColor = {
  pending: "bg-amber-500/20 text-amber-300",
  approved: "bg-emerald-500/20 text-emerald-300",
  rejected: "bg-red-500/20 text-red-300",
};
const TYPES = ["vacation", "sick", "personal", "unpaid", "bereavement"];

export default function Leave() {
  const { user } = useAuth();
  const isMgr = user?.role === "manager" || user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ type: "vacation", start_date: "", end_date: "", reason: "" });

  const load = async () => setItems((await api.get("/leave")).data);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (form.end_date < form.start_date) { toast.error("End date can't be before the start date."); return; }
    setBusy(true);
    try {
      await api.post("/leave", form);
      toast.success("Leave request submitted.");
      setOpen(false); setForm({ type: "vacation", start_date: "", end_date: "", reason: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const decide = async (l, decision) => {
    try { await api.post(`/leave/${l.id}/decision`, { decision }); toast.success(`Leave ${decision}.`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const pending = items.filter((l) => l.status === "pending");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Plane className="w-5 h-5 text-emerald-400" />
          <h1 className="font-display text-2xl font-bold">Leave & Time Off</h1>
        </div>
        {!isMgr && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="new-leave-btn"><Plus className="w-4 h-4 mr-1" /> Request leave</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121816] border-emerald-500/20">
              <DialogHeader><DialogTitle>Request time off</DialogTitle>
                <DialogDescription>Pick the dates and type of leave. Your manager will review it.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20 capitalize" data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121816] border-emerald-500/20">
                      {TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>From</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="leave-start-input" /></div>
                  <div><Label>To</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="leave-end-input" /></div>
                </div>
                <div><Label>Reason</Label><Textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="leave-reason-input" /></div>
              </div>
              <DialogFooter>
                <Button onClick={submit} disabled={busy || !form.start_date || !form.end_date} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="leave-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit request"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isMgr && pending.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs uppercase tracking-widest text-muted-foreground">Pending approval</h3>
          {pending.map((l) => (
            <div key={l.id} className="card-emerald p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="leave-pending-card">
              <div>
                <p className="font-medium capitalize">{l.name} · {l.type} · {l.days} day(s)</p>
                <p className="text-sm text-muted-foreground">{l.start_date} → {l.end_date}</p>
                {l.reason && <p className="text-sm text-emerald-200/80 mt-1">"{l.reason}"</p>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => decide(l, "approved")} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217]" data-testid="leave-approve-btn"><Check className="w-4 h-4 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" onClick={() => decide(l, "rejected")} className="border-red-500/40 text-red-300" data-testid="leave-reject-btn"><X className="w-4 h-4 mr-1" /> Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card-emerald overflow-hidden">
        <div className="px-5 pt-5 font-display font-semibold">{isMgr ? "All requests" : "My requests"}</div>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm" data-testid="leave-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-white/10">
                {isMgr && <th className="py-3 px-5">Employee</th>}
                <th className="py-3 px-3">Type</th><th className="py-3 px-3">Dates</th>
                <th className="py-3 px-3">Days</th><th className="py-3 px-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={isMgr ? 5 : 4} className="py-10 text-center text-muted-foreground">No leave requests yet.</td></tr>
              ) : items.map((l) => (
                <tr key={l.id} className="border-b border-white/5" data-testid="leave-row">
                  {isMgr && <td className="py-3 px-5">{l.name}</td>}
                  <td className="py-3 px-3 capitalize">{l.type}</td>
                  <td className="py-3 px-3 text-muted-foreground">{l.start_date} → {l.end_date}</td>
                  <td className="py-3 px-3">{l.days}</td>
                  <td className="py-3 px-3"><Badge className={statusColor[l.status]}>{l.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
